import React, { useEffect, useRef, useState } from "react";
import "../../VideoManager.css";
import { ensureMandatoryTag, MANDATORY_TAG } from "../../utils/videoManagerUtils";
import uploadVideoEndpoint from "../../config/uploadApi";

const formatFileName = () => `gcc-recording-${new Date().toISOString().replace(/[:.]/g, "-")}.webm`;

const RecordingStudio = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [statusMessage, setStatusMessage] = useState("Ready to capture your worship flow.");
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploadTitle, setUploadTitle] = useState("Worship Mix Capture");
  const [uploadDescription, setUploadDescription] = useState(
    "Ready-to-publish capture recorded with GCC Video Manager.",
  );
  const [tags, setTags] = useState(ensureMandatoryTag(["worship-recordings"]));
  const [tagInput, setTagInput] = useState("");
  const [uploadStatus, setUploadStatus] = useState("");
  const [recordingUrl, setRecordingUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [audioMicLevel, setAudioMicLevel] = useState(70);
  const [audioSystemLevel, setAudioSystemLevel] = useState(80);
  const [recordingFileName, setRecordingFileName] = useState(formatFileName());
  const [showRecordedPreview, setShowRecordedPreview] = useState(true);
  const [micEnabled, setMicEnabled] = useState(false);
  const [micCapturedViaSystem, setMicCapturedViaSystem] = useState(false);

  const recorderRef = useRef(null);
  const streamsRef = useRef([]);
  const chunksRef = useRef([]);
  const previewUrlRef = useRef("");
  const audioContextRef = useRef(null);
  const destinationRef = useRef(null);
  const systemGainRef = useRef(null);
  const micGainRef = useRef(null);

  useEffect(() => {
    return () => {
      if (recorderRef.current?.state === "recording") recorderRef.current.stop();
      cleanupStreams();
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const cleanupStreams = () => {
    streamsRef.current.forEach((stream) => stream.getTracks().forEach((track) => track.stop()));
    streamsRef.current = [];
    if (destinationRef.current) {
      destinationRef.current.stream.getTracks().forEach((track) => track.stop());
      destinationRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    systemGainRef.current = null;
    micGainRef.current = null;
  };

  useEffect(() => {
    if (systemGainRef.current) {
      systemGainRef.current.gain.value = audioSystemLevel / 100;
    }
  }, [audioSystemLevel]);

  useEffect(() => {
    if (micGainRef.current) {
      micGainRef.current.gain.value = audioMicLevel / 100;
    }
  }, [audioMicLevel]);

  const startRecording = async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getDisplayMedia) {
      setStatusMessage("Screen capture not supported by this browser.");
      return;
    }

    if (typeof MediaRecorder === "undefined") {
      setStatusMessage("MediaRecorder API is unavailable.");
      return;
    }

    setMicCapturedViaSystem(false);
    setMicEnabled(false);
    setStatusMessage("Requesting screen permissions…");

    try {
      const videoConstraints = {
        cursor: "always",
        frameRate: { ideal: 30 },
      };

      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: videoConstraints,
        audio: true,
      });
      streamsRef.current.push(displayStream);

      const mixedStream = new MediaStream();
      displayStream.getVideoTracks().forEach((track) => mixedStream.addTrack(track));

      const displayAudioTracks = displayStream.getAudioTracks();
      const looksLikeMicCapture = (track) => {
        const label = track?.label?.toLowerCase() ?? "";
        return /mic(rophone)?|microphone|headset|input|line in|voice/.test(label);
      };
      const displayAudioIncludesMic = displayAudioTracks.some(looksLikeMicCapture);
      setMicCapturedViaSystem(displayAudioIncludesMic);

      let audioContext;
      let audioDestination;
      let usingAudioContext = false;
      const AudioConstructor =
        typeof window !== "undefined" ? window.AudioContext || window.webkitAudioContext : null;
      if (AudioConstructor) {
        try {
          audioContext = new AudioConstructor();
          audioContextRef.current = audioContext;
          audioDestination = audioContext.createMediaStreamDestination();
          destinationRef.current = audioDestination;

          if (displayStream.getAudioTracks().length) {
            const systemSource = audioContext.createMediaStreamSource(displayStream);
            const systemGain = audioContext.createGain();
            systemGain.gain.value = audioSystemLevel / 100;
            systemSource.connect(systemGain).connect(audioDestination);
            systemGainRef.current = systemGain;
          }

          const destinationTrack = audioDestination.stream.getAudioTracks()[0];
          if (destinationTrack) {
            mixedStream.addTrack(destinationTrack);
            usingAudioContext = true;
          }
        } catch (audioSetupError) {
          console.warn("Audio context setup failed", audioSetupError);
          audioContext?.close();
          audioContextRef.current = null;
          destinationRef.current = null;
          systemGainRef.current = null;
          micGainRef.current = null;
          usingAudioContext = false;
        }
      }

      if (!usingAudioContext) {
        displayStream.getAudioTracks().forEach((track) => mixedStream.addTrack(track));
      }

      if (!displayAudioIncludesMic) {
        try {
          const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          streamsRef.current.push(micStream);
          setMicEnabled(true);
          if (usingAudioContext && audioContext && audioDestination) {
            const micSource = audioContext.createMediaStreamSource(micStream);
            const micGain = audioContext.createGain();
            micGain.gain.value = audioMicLevel / 100;
            micSource.connect(micGain).connect(audioDestination);
            micGainRef.current = micGain;
          } else {
            micStream.getAudioTracks().forEach((track) => mixedStream.addTrack(track));
          }
        } catch (micError) {
          console.warn("Microphone capture failed", micError);
          setStatusMessage("Mic blocked, capturing screen audio only.");
          setMicEnabled(false);
        }
      } else if (usingAudioContext) {
        setStatusMessage("System audio already includes mic input; keeping display stream to avoid echo.");
      }

      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
        ? "video/webm;codecs=vp9"
        : "video/webm";

      chunksRef.current = [];
      const recorder = new MediaRecorder(mixedStream, { mimeType });
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data?.size) chunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        setIsRecording(false);
        cleanupStreams();
        setMicCapturedViaSystem(false);
        setMicEnabled(false);
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        chunksRef.current = [];
        setRecordedBlob(blob);
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        const newUrl = URL.createObjectURL(blob);
        previewUrlRef.current = newUrl;
        setPreviewUrl(newUrl);
        const newFileName = formatFileName();
        setRecordingFileName(newFileName);
        setStatusMessage("Recording complete.");
        setShowRecordedPreview(true);
        if (typeof window !== "undefined") {
          window.gccLatestRecording = { blob, fileName: newFileName };
          window.dispatchEvent(
            new CustomEvent("gccRecordingReady", {
              detail: { blob, fileName: newFileName },
            }),
          );
        }
      };

      recorder.start(250);
      setIsRecording(true);
      setStatusMessage("Recording… tap stop to end capture.");
    } catch (error) {
      console.error("Recording failed", error);
      cleanupStreams();
      setMicCapturedViaSystem(false);
      setMicEnabled(false);
      setIsRecording(false);
      setStatusMessage("Capture canceled or permission denied.");
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      recorderRef.current?.stop();
      return;
    }
    startRecording();
  };

  const handleAddTag = () => {
    const newTag = tagInput.trim();
    if (!newTag) return;
    setTags((prev) => ensureMandatoryTag([...prev, newTag]));
    setTagInput("");
  };

  const handleUploadRequest = async () => {
    if (!recordedBlob) {
      setUploadStatus("Record before uploading.");
      return;
    }
    setIsUploading(true);
    setUploadStatus("Uploading to @gcc-worshop…");
    const formData = new FormData();
    formData.append("video", recordedBlob, recordingFileName || formatFileName());
    formData.append("title", uploadTitle);
    formData.append("description", uploadDescription);
    formData.append("privacy", "unlisted");
    formData.append("tags", JSON.stringify(ensureMandatoryTag(tags)));

    try {
      const res = await fetch(uploadVideoEndpoint, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || "Upload failed.");
      }
      const payload = await res.json();
      setRecordingUrl(payload.videoUrl);
      setUploadStatus(`Upload complete. ${payload.videoUrl}`);
    } catch (error) {
      console.error("Upload failed", error);
      setUploadStatus(`Upload failed: ${error.message || "Unknown error"}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleTagChipRemove = (tagToRemove) => {
    if (tagToRemove === MANDATORY_TAG) return;
    setTags((prev) => prev.filter((tag) => tag !== tagToRemove));
  };

  const handleDownloadRecording = () => {
    if (!previewUrl) return;
    const anchor = document.createElement("a");
    anchor.href = previewUrl;
    anchor.download = recordingFileName || formatFileName();
    anchor.click();
  };

  const previewButtonLabel = showRecordedPreview ? "Hide preview" : "Preview clip";
  const previewAvailable = Boolean(previewUrl);
  const activeSourceList = [
    micCapturedViaSystem
      ? "Mic via system audio"
      : micEnabled
      ? "Mic audio"
      : "Mic disabled",
    audioSystemLevel > 0 ? "System audio" : "System muted",
  ];

  return (
    <section className="studio-panel recording-studio" aria-label="Screen recording studio">
      <div className="recording-studio__layout">
        <div className="studio-panel__controls-column">
          <div className="recording-studio__control-block studio-panel__control-block">
            <div className="recording-studio__label">Transport</div>
            <div className="recording-studio__transport-actions">
              <button
                type="button"
                className={`studio-panel__cta recording${isRecording ? " recording" : ""}`}
                onClick={toggleRecording}
              >
                {isRecording ? "Stop recording" : "Start recording"}
              </button>
            </div>
            <span className="studio-panel__helper">{statusMessage}</span>
          </div>

          <div className="recording-studio__control-block studio-panel__control-block">
            <div className="recording-studio__label">Audio mixer</div>
            <div className="recording-studio__mixer-row">
              <label>
                Microphone
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={audioMicLevel}
                  onChange={(event) => setAudioMicLevel(Number(event.target.value))}
                />
              </label>
              <span>{audioMicLevel}%</span>
            </div>
            <div className="recording-studio__mixer-row">
              <label>
                System audio
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={audioSystemLevel}
                  onChange={(event) => setAudioSystemLevel(Number(event.target.value))}
                />
              </label>
              <span>{audioSystemLevel}%</span>
            </div>
          </div>
        </div>

        <div className="studio-panel__preview-column recording-studio__output">
          <div className="recording-studio__preview-grid">
            <div className="recording-studio__preview-panel">
              <div className="recording-studio__review-heading">Preview</div>
              {previewAvailable && showRecordedPreview ? (
                <video controls src={previewUrl} className="recording-studio__live-preview" playsInline />
              ) : (
                <div className="recording-studio__placeholder">
                  {previewAvailable
                    ? "Preview paused. Toggle the button below."
                    : "Recording preview will appear here."}
                </div>
              )}
              <div className="studio-panel__preview-actions">
                {previewAvailable && (
                  <button
                    type="button"
                    className="studio-panel__cta studio-panel__cta--secondary"
                    onClick={() => setShowRecordedPreview((prev) => !prev)}
                  >
                    {previewButtonLabel}
                  </button>
                )}
                {previewAvailable && (
                  <button
                    type="button"
                    className="studio-panel__cta studio-panel__cta--secondary"
                    onClick={handleDownloadRecording}
                  >
                    Download clip
                  </button>
                )}
              </div>
            </div>
            <div className="recording-studio__preview-panel">
              <div className="recording-studio__review-heading">Program</div>
              <p className="studio-panel__helper">Layers: {activeSourceList.join(" · ")}</p>
              <p className="studio-panel__helper">Status: {statusMessage}</p>
            </div>
          </div>

          <form
            className="studio-panel__form recording-studio__upload-form"
            onSubmit={(event) => {
              event.preventDefault();
              handleUploadRequest();
            }}
          >
            <label>
              <span>Title</span>
              <input value={uploadTitle} onChange={(event) => setUploadTitle(event.target.value)} />
            </label>
            <label>
              <span>Description</span>
              <textarea
                rows={3}
                value={uploadDescription}
                onChange={(event) => setUploadDescription(event.target.value)}
              />
            </label>
            <label>
              <span>Tags</span>
              <div className="studio-panel__tag-row">
                <input
                  value={tagInput}
                  onChange={(event) => setTagInput(event.target.value)}
                  placeholder="Add a tag, e.g. worship, session"
                />
                <button type="button" onClick={handleAddTag}>
                  Add tag
                </button>
              </div>
            </label>
            <div className="studio-panel__tag-list">
              {ensureMandatoryTag(tags).map((tag) => (
                <span key={tag} className="studio-panel__tag">
                  {tag}
                  {tag !== MANDATORY_TAG && (
                    <button type="button" onClick={() => handleTagChipRemove(tag)}>
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
            <button type="submit" className="studio-panel__cta" disabled={isUploading}>
              {isUploading ? "Uploading…" : "Upload to @gcc-worshop"}
            </button>
            {uploadStatus && <p className="studio-panel__helper">{uploadStatus}</p>}
            {recordingUrl && (
              <a href={recordingUrl} className="studio-panel__helper" target="_blank" rel="noreferrer">
                View uploaded clip
              </a>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default RecordingStudio;
